// biome-ignore-all lint/suspicious/noArrayIndexKey: index keys are safe here; lists are derived from render state in a stable order and items are not reordered or removed mid-render (Task #32).
// biome-ignore-all lint/a11y/useSemanticElements: role="button" is intentional; the element wraps nested interactive children, so it cannot be a native button (Task #25).
// =============================================================================
// ApprovalDecisions.jsx
// =============================================================================
// The main approval workspace for staff/responders. Renders a collapsible
// summary panel listing every item (fees, uniforms, shoes, device) across
// every child in the request, each with its current approval status. The
// actual per-item approve/decline controls live in the per-child view above;
// this component is the high-level decision summary plus the two big buttons:
//   - Revert to Pending — undo all decisions made this session
//   - Confirm & Send to DFV Service — finalise + email + audit + auto-complete tasks
//
// Pulled out of RequestDetail.jsx as part of the Phase C extraction pass.
// ~960 lines. Pure helpers it depends on
// (applyApprovalStatuses, stampConfirmedAt, buildApprovalSummaryText,
// buildPostSaveSnapshot) live in services/approvals.js. Overall status is
// derived through requestLifecycleReducer (services/requestLifecycle.js), the
// guarded client-side state machine — not by calling deriveOverallStatus directly.
//
// Props:
//   req                       - the request being approved
//   user                      - logged-in staff user (audit + role checks)
//   onSave                    - called with the updated request after save
//   showSaved                 - toast helper
//   openCommentWithTemplate   - opens the comment panel pre-filled (template flow)
//   onSchoolLockerApproved    - callback when a School Locker uniform is approved
// =============================================================================
import React from "react";
import { canAccess, isAdminRole, isSuperUser } from "../../app/access";
import { useSession } from "../../app/SessionContext.jsx";
import { recordRequestDecisions } from "../../data/requestDecisions";
import { insertInternalNote } from "../../data/requestInternalNotes";
import {
  appendRequestNotes,
  getRequestChildren,
  getRequestNotesById,
  getRequestViewRowById,
  saveRequestChildren,
  updateRequestByIdIfConfirmedAtMatches,
} from "../../data/requests";
import { completeTasksForRef } from "../../data/requestTasks";
import { useCommentTemplates } from "../../hooks/useCommentTemplates";
import { useShelterApprovalHold } from "../../hooks/useShelterApprovalHold";
import { hasApprovedOrDeclinedItems } from "../../services/approvalHold";
import {
  applyApprovalStatuses,
  buildApprovalSummaryText,
  buildPostSaveSnapshot,
  isDecided,
  isLockedFullyProcessed,
  stampConfirmedAt,
} from "../../services/approvals";
import { buildDecisionRows, snapshotDecisionBaseline } from "../../services/decisionLog";
import { confirmDispatchBlockReason } from "../../services/deliveryApprovalHold";
import { alertDialog, confirmDialog } from "../../services/dialogService";
import {
  flaggedItemsForConfirm,
  REPEAT_NOTE_KIND,
  repeatNoteText,
} from "../../services/repeatCheck";
import { requestStamp } from "../../services/requestStamp";
import { queueSalesOrderForRequest } from "../../services/salesOrderCreation";
import { sendEmail } from "../../utils/actions";
import { logAudit } from "../../utils/auditLog";
import { APPROVAL_STATUSES, REQUEST_STATUSES } from "../../utils/enums";
import { calcUniItems } from "../../utils/formConstants";
import { GLYPH } from "../../utils/glyphs";
import { dbToReq, money } from "../../utils/helpers";
import { logger } from "../../utils/logger";
import { HelpButton } from "../form/ChildSections.jsx";
import { RepeatCheckNoteModal } from "./RepeatCheckNoteModal.jsx";
import { APPROVAL_COLOURS } from "./RequestDetailAtoms.jsx";

export function ApprovalDecisions({
  req,
  onSave,
  showSaved,
  openCommentWithTemplate,
  onSchoolLockerApproved,
  deriveStatus,
  onConfirmed,
  // Possible-repeat flag (db-210): ItemRepeat map by fee/device id from useRepeatCheck.
  // When any item on the request is flagged, Confirm & Send requires a Repeat check note.
  repeats = null,
  // Optional async () => fresh ItemRepeat map | null (RequestDetail passes loadFreshRepeats).
  // Called at Confirm & Send so a twin submitted while the screen was open is not missed;
  // null (failed / slow lookup) falls back to `repeats`.
  refreshRepeats = null,
}) {
  const { user } = useSession();
  // The open Repeat check pop-up: { items, resolve } while waiting for the note, else null.
  const [repeatAsk, setRepeatAsk] = React.useState(null);
  // True while the fresh repeat lookup runs, so a double-click can't start two confirms.
  const repeatRefreshingRef = React.useRef(false);
  // Monitored-shelter approval hold (backstop): if the shelter is flagged
  // no_approvals, anyone WITHOUT the monitored_approval capability can't Confirm &
  // Send while any item is Approved/Declined. See src/services/approvalHold.ts.
  const approvalHold = useShelterApprovalHold(req.shelterCode, req.shelterName);
  // Who may approve a monitored service — a SuperUser or the monitored_approval
  // capability (mirrors the per-item gate in ResponderChildView). Was any admin
  // until 6 Sep 2026. See docs/permissions-and-approval-hold-plan.md §Part 1.
  const canApproveMonitored = isSuperUser(user) || canAccess(user, "monitored_approval");
  // ── APPROVAL STATE: { childIdx: { fees: ["Pending",...], uniforms:"Pending", shoes:"Pending", laptop:"Pending" } }

  function initApprovals() {
    const state = {};
    (req.children || []).forEach(function (child, ci) {
      state[ci] = {
        fees: (child.fees || []).map(function (f) {
          return f.approvalStatus || APPROVAL_STATUSES.PENDING;
        }),
        uniforms: child.uniforms
          ? child.uniforms.approvalStatus || APPROVAL_STATUSES.PENDING
          : null,
        shoes: child.shoes ? child.shoes.approvalStatus || APPROVAL_STATUSES.PENDING : null,
        laptop: child.laptop ? child.laptop.approvalStatus || APPROVAL_STATUSES.PENDING : null,
      };
    });
    return state;
  }

  const approvalState = React.useState(initApprovals);
  const approvals = approvalState[0];
  const setApprovals = approvalState[1];
  const saving = React.useState(false);
  const isSaving = saving[0];
  const setIsSaving = saving[1];

  const _confirming = React.useState(false);
  const isConfirming = _confirming[0];
  const setIsConfirming = _confirming[1];

  // Default to collapsed — responder expands when they want to act on it.
  const _collapsed = React.useState(true);
  const approvalsCollapsed = _collapsed[0];
  const setApprovalsCollapsed = _collapsed[1];

  const confirmedSnapshotRef = React.useRef(null);

  // Returns true if this item has a decision (Approved/Declined/Needs Info) that
  // has NOT yet been confirmed-and-sent. We use the item's own `confirmedAt` stamp
  // as the anchor, NOT req.approvalsConfirmedAt (which gets cleared to null whenever
  // a new decision is made, even if previous sessions were confirmed). An item is
  // NEW if it's decided and has never been stamped via Confirm & Send.
  function isNewSinceConfirm(ci, catKey, fi) {
    const child = (req.children || [])[ci];
    if (!child) return false;
    let item = null;
    if (catKey === "fees") item = child.fees?.[fi];
    else if (catKey === "uniforms") item = child.uniforms;
    else if (catKey === "shoes") item = child.shoes;
    else if (catKey === "laptop") item = child.laptop;
    if (!item) return false;
    if (!isDecided(item.approvalStatus)) return false;
    // NEW = decided but never confirmed-and-sent (no confirmedAt stamp).
    // Items confirmed in any earlier session have a confirmedAt and stay quiet.
    return !item.confirmedAt;
  }

  const _tdb = useCommentTemplates();
  const allTemplates = _tdb.templates;
  const templatesLoading = _tdb.loading;

  // Snapshot at session start — only set on req.id change, never overwritten by status changes
  const sessionStartApprovalsRef = React.useRef(null);

  // Re-sync when req changes (e.g. after ResponderChildView saves an individual approval)
  const _childrenKey = JSON.stringify(
    (req.children || []).map(function (c) {
      return {
        fees: (c.fees || []).map(function (f) {
          return f.approvalStatus;
        }),
        u: c.uniforms?.approvalStatus,
        s: c.shoes?.approvalStatus,
        l: c.laptop?.approvalStatus,
      };
    })
  );
  React.useEffect(
    function () {
      setApprovals(initApprovals());
      // If this request has been confirmed, build the confirmed snapshot from current state
      // (items approved before last confirm should NOT show "New" badge on load)
      if (req.approvalsConfirmedAt && !confirmedSnapshotRef.current) {
        confirmedSnapshotRef.current = initApprovals();
      }
    },
    [req.id, _childrenKey]
  );
  // Track the approvalsConfirmedAt at session start so Revert can restore it
  const sessionStartConfirmedAtRef = React.useRef(null);
  // Optimistic-concurrency baseline: the DB approvals_confirmed_at this editor opened with.
  // Confirm & Send does a compare-and-swap against it, so a competing confirm (another tab /
  // user / device) is rejected instead of silently overwritten. Re-baselined after each
  // successful confirm; kept SEPARATE from sessionStartConfirmedAtRef (used by Revert) so it
  // can move post-confirm without changing Revert's restore target.
  const dbConfirmedAtRef = React.useRef(null);
  // Decision log (db-207): each item's status + reason at session start, so Confirm &
  // Send can record the from→to step per item. Kept SEPARATE from
  // sessionStartApprovalsRef (statuses only, used by the summary + Revert) so neither
  // existing behaviour changes. Re-baselined after each successful confirm.
  const decisionBaselineRef = React.useRef(null);
  // Capture session-start snapshot only on request change (not on status changes)
  React.useEffect(
    function () {
      sessionStartApprovalsRef.current = initApprovals();
      decisionBaselineRef.current = snapshotDecisionBaseline(req.children);
      sessionStartConfirmedAtRef.current = req.approvalsConfirmedAt || null;
      dbConfirmedAtRef.current = req.approvalsConfirmedAt || null;
    },
    [req.id]
  );

  // (Dead inline-edit setters removed — the inline-edit UI was retired earlier, so
  // this panel is display + confirm/revert only. All decisions are made in
  // ResponderChildView, which syncs here via the in-memory req.)

  // Revert decisions made in THIS session back to their session-start state.
  // Items confirmed in earlier sessions (with confirmedAt stamps) are NOT touched.
  async function revertToPending() {
    if (templatesLoading) return;
    // D-2/D-4 terminal lock (defense-in-depth; the button is already disabled).
    if (isLockedFullyProcessed(req)) return;
    const snap = sessionStartApprovalsRef.current;
    if (!snap) {
      await alertDialog({
        message: "Cannot determine original state. Please refresh the page and try again.",
      });
      return;
    }
    if (
      !(await confirmDialog({
        title: "Revert decisions?",
        message:
          "Revert any decisions you made this session back to their original status?\n\nApprovals confirmed in previous sessions will NOT be touched.",
        confirmLabel: "Revert",
      }))
    )
      return;
    setIsSaving(true);
    try {
      const updatedReq = Object.assign({}, req);
      updatedReq.children = (req.children || []).map(function (child, ci) {
        const origSnap = snap[ci] || {};
        const updated = Object.assign({}, child);
        updated.fees = (child.fees || []).map(function (f, fi) {
          const origStatus = origSnap.fees?.[fi] || APPROVAL_STATUSES.PENDING;
          return Object.assign({}, f, { approvalStatus: origStatus });
        });
        if (child.uniforms)
          updated.uniforms = Object.assign({}, child.uniforms, {
            approvalStatus: origSnap.uniforms || APPROVAL_STATUSES.PENDING,
          });
        if (child.shoes)
          updated.shoes = Object.assign({}, child.shoes, {
            approvalStatus: origSnap.shoes || APPROVAL_STATUSES.PENDING,
          });
        if (child.laptop)
          updated.laptop = Object.assign({}, child.laptop, {
            approvalStatus: origSnap.laptop || APPROVAL_STATUSES.PENDING,
          });
        return updated;
      });
      // Re-derive overall status from the now-reverted children, so a request
      // that had previously confirmed approvals stays Approved/Partial; one
      // that has nothing left approved goes back to Submitted.
      // Route through the guarded lifecycle reducer (mirror of the db-8 transition
      // guard) instead of calling deriveOverallStatus directly — one client-side
      // owner of status. Behaviour-identical here: revert always has items, so
      // DERIVE never yields null (the old `|| SUBMITTED` fallback was unreachable).
      const revertedStatus = deriveStatus(updatedReq.children);

      // Recompute approvalsConfirmedAt from the surviving items' own stamps.
      // If every DECIDED item has a confirmedAt, the request is fully confirmed
      // up to the most recent of those stamps, so the Confirm & Send button should
      // re-lock. If any decided item is unstamped, leave null so it stays enabled.
      const decidedItems = [];
      let anyUnstamped = false;
      (updatedReq.children || []).forEach(function (child) {
        (child.fees || []).forEach(function (f) {
          if (isDecided(f.approvalStatus)) {
            if (f.confirmedAt) decidedItems.push(f.confirmedAt);
            else anyUnstamped = true;
          }
        });
        ["uniforms", "shoes", "laptop"].forEach(function (k) {
          const it = child[k];
          if (it && isDecided(it.approvalStatus)) {
            if (it.confirmedAt) decidedItems.push(it.confirmedAt);
            else anyUnstamped = true;
          }
        });
      });
      let restoredConfirmedAt;
      if (decidedItems.length === 0) {
        // No decisions at all — revert truly clears everything
        restoredConfirmedAt = null;
      } else if (anyUnstamped) {
        // Some decided items haven't been confirmed yet — leave null
        restoredConfirmedAt = null;
      } else {
        // All decided items have stamps — pick the latest as the confirm time
        restoredConfirmedAt = decidedItems.reduce(function (latest, t) {
          return !latest || new Date(t) > new Date(latest) ? t : latest;
        }, null);
      }

      // Ephemeral staging: reverting just discards this session's in-memory
      // decisions — nothing was persisted, so there is no DB write. The in-memory
      // request is restored to the session-start snapshot via onSave below.
      // Restore local approvals state to the session-start snapshot
      setApprovals(snap);
      const savedReq = Object.assign({}, updatedReq, {
        status: revertedStatus,
        updatedBy: user.shelterName,
        updatedAt: new Date().toISOString(),
        approvalsConfirmedAt: restoredConfirmedAt,
      });
      onSave(savedReq);
      if (showSaved) showSaved("Session decisions reverted");
    } catch (e) {
      logger.error("revertToPending error:", e);
    } finally {
      setIsSaving(false);
    }
  }

  async function confirmAndSend() {
    if (templatesLoading) return;
    let updatedReq = applyApprovalStatuses(req, approvals);
    // Monitored-shelter backstop: a responder (non-admin) can't send while any
    // item is Approved/Declined on a flagged shelter — only Needs Info is allowed.
    if (
      approvalHold.noApprovals &&
      !canApproveMonitored &&
      hasApprovedOrDeclinedItems(updatedReq.children)
    ) {
      await alertDialog({ message: approvalHold.message });
      return;
    }
    // Delivery backstop (Batch 5 + db-147): don't send with a Device or Stationery
    // Pack approved while the request's delivery address is pending verification OR
    // banned — those are the two items Zephyr dispatches. No admin exemption (a
    // data-integrity gate, not an escalation). Banned takes priority in the message.
    const dispatchBlockReason = confirmDispatchBlockReason({
      deliveryAddressVerified: req.deliveryAddressVerified,
      deliveryAddressBanned: req.deliveryAddressBanned,
      children: updatedReq.children,
    });
    if (dispatchBlockReason) {
      await alertDialog({ message: dispatchBlockReason });
      return;
    }
    // Possible-repeat flag (db-210): a flagged request needs a Repeat check note before
    // ANYTHING is saved. Cancel = return with nothing written (ephemeral staging). The
    // note is saved further down, only once the confirm itself has succeeded.
    // Look the matches up again first (fix 24 Oct): the on-screen flags were loaded when the
    // request opened and can miss a twin submitted since. Best-effort — fall back to them.
    if (repeatRefreshingRef.current) return;
    let currentRepeats = repeats;
    if (refreshRepeats) {
      repeatRefreshingRef.current = true;
      try {
        const fresh = await refreshRepeats();
        if (fresh) currentRepeats = fresh;
      } catch (e) {
        logger.warn("refreshRepeats failed:", e?.message || e);
      } finally {
        repeatRefreshingRef.current = false;
      }
    }
    const flaggedForNote = flaggedItemsForConfirm(updatedReq.children, currentRepeats);
    let repeatNote = null;
    if (flaggedForNote.length > 0) {
      repeatNote = await new Promise(function (resolve) {
        setRepeatAsk({ items: flaggedForNote, resolve: resolve });
      });
      setRepeatAsk(null);
      if (!repeatNote) return;
    }
    let repeatNoteFailed = false;
    setIsConfirming(true);
    try {
      // Derive via the guarded lifecycle reducer (single client-side owner of status).
      const newStatus = deriveStatus(updatedReq.children);
      const confirmedAt = new Date().toISOString();

      // Decision log (db-207): work out the per-item rows NOW — it needs the items
      // BEFORE stampConfirmedAt below. Pure, and guarded so it can never stop a
      // confirm; the rows are only SENT at the very end, after everything else.
      let decisionRows = [];
      try {
        decisionRows = buildDecisionRows(decisionBaselineRef.current, updatedReq.children);
      } catch (e) {
        logger.warn("buildDecisionRows failed:", e?.message || e);
      }

      // Stamp confirmedAt on each item being confirmed as Approved
      // This lets us detect on reload which items were confirmed in which session.
      // stampConfirmedAt preserves existing confirmedAt values from previous sessions.
      updatedReq = stampConfirmedAt(updatedReq, confirmedAt);

      // Local snapshot the post-confirm `notes` state is built from. The approval
      // summary is appended to the DB atomically below (db-200), so this base is
      // never written back as a whole array — it only feeds local state.
      const updatedNotes = (await getRequestNotesById(req.id)) ?? (req.notes || []);

      // Per-item instructions are shown via Action Sheets in the shelter
      // view, so the only comment we stage is the high-level summary below.
      const prevConfirmedAt = req.approvalsConfirmedAt; // PREVIOUS confirm time (before this session)
      const stagedDrafts = [];

      // Build the summary comment text. Greeting/headers/closing/signoff are
      // sourced from comment_templates rows ("_sum_*" ids) so admins can edit
      // copy via the Admin → Templates page without a code deploy.
      // sessionStartApprovalsRef holds the original statuses for "newly
      // declined" detection (req.children has been mutated by onSaveNoDb during
      // the session).
      const summaryText = buildApprovalSummaryText({
        updatedReq: updatedReq,
        sessionStartApprovals: sessionStartApprovalsRef.current,
        prevConfirmedAt: prevConfirmedAt,
        confirmedAt: confirmedAt,
        allTemplates: allTemplates,
        refNoFallback: req.refNo,
      });
      stagedDrafts.push({
        author: user.shelterName,
        text: summaryText,
        date: new Date().toLocaleString("en-AU"),
        ts: new Date().toISOString(),
        _summary: true,
        byRole: "staff",
        // Thread colour tier — a supervisor's confirm reads blue, a responder's teal.
        tier: isAdminRole(user) ? "supervisor" : "responder",
      });

      // Local-only snapshot for the post-confirm `notes` state update below. The
      // summary is appended to the DB atomically after the CAS (db-200); it is never
      // written back as a whole array, so it can't clobber a concurrent comment.
      const notesWithDrafts = updatedNotes.concat(stagedDrafts);
      // NB: `notes` is deliberately NOT in this compare-and-swap payload. The summary
      // is appended atomically AFTER the CAS (db-200), so a comment added since this
      // approver opened the request can't be clobbered by the confirm (nor vice-versa).
      // The CAS still guards status / approvals_confirmed_at below.
      // See docs/notes-atomic-append-plan.md.
      const updatePayload = {
        // Was a hard-coded "staff" — correct, because only staff reach Confirm &
        // Send, but routed through the shared helper so nothing writes these two
        // columns by hand (src/services/requestStamp.ts).
        ...requestStamp(user),
        approvals_confirmed_at: confirmedAt,
        // Confirm & Send posts the approval summary to the DFV Service → mark the
        // request as having an unread staff comment (db-77). Cleared when the
        // shelter opens the Comments panel on their request.
        shelter_has_unread_comment: true,
      };
      if (newStatus) updatePayload.status = newStatus;
      const result = await updateRequestByIdIfConfirmedAtMatches(
        req.id,
        dbConfirmedAtRef.current,
        updatePayload
      );
      if (result.error) {
        logger.warn("Confirm failed:", result.error.message);
        return;
      }
      if (!result.updated) {
        // Compare-and-swap miss: another tab / responder / device confirmed this request
        // since we opened it. Reject rather than overwrite — nothing has been sent yet.
        // Re-sync from the DB so the stale local staging is dropped and the user sees the
        // latest (the other confirm); re-baseline so a fresh decision can be confirmed after.
        const freshRow = await getRequestViewRowById(req.id);
        if (freshRow) {
          const freshReq = dbToReq(freshRow);
          dbConfirmedAtRef.current = freshReq.approvalsConfirmedAt || null;
          onSave(freshReq);
        }
        await alertDialog({
          title: "This request changed",
          message:
            "This request was confirmed in another tab or by another responder since you opened it.\n\n" +
            "Your decisions were NOT sent. The latest version has been reloaded — please re-review before confirming.",
        });
        return;
      }
      // CAS succeeded — this confirm is the new DB baseline for any further confirm this session.
      dbConfirmedAtRef.current = confirmedAt;
      // Append the approval summary atomically (db-200) now the CAS has committed the
      // status/approval. Best-effort: a summary-append failure is logged only (the
      // approval itself is already saved), and it can no longer clobber — or be
      // clobbered by — a concurrent comment.
      if (stagedDrafts.length) {
        const noteRes = await appendRequestNotes(req.id, stagedDrafts, {
          updatedBy: user.shelterName,
          markUnread: true,
        });
        if (noteRes.error) logger.warn("Append approval summary failed:", noteRes.error.message);
      }
      // Repeat check note (db-210) — staff-only internal note, written only now that the
      // confirm has committed, so a rejected confirm never leaves an orphan note.
      if (repeatNote) {
        const rn = await insertInternalNote({
          requestId: req.id,
          author: user.shelterName,
          authorId: user.id || null,
          text: repeatNoteText(repeatNote),
          kind: REPEAT_NOTE_KIND,
        });
        if (rn.error) repeatNoteFailed = true;
      }
      // Stage 1: persist children via RPC
      const rpcRes2 = await saveRequestChildren(req.id, updatedReq.children);
      if (rpcRes2.error) {
        logger.warn("save_request_children failed:", rpcRes2.error.message);
        return;
      }
      logAudit(
        "approvals_confirmed",
        {
          request_id: req.id,
          ref: req.refNo,
          status: newStatus || req.status,
          by: user.shelterName,
        },
        req.id
      );

      // Only auto-complete the request's responder tasks once EVERY item has been
      // actioned (Approved / Declined / Needs Info). If ANY item is still Pending, the
      // task(s) stay on the list so the responder returns to finish it (John, 30 Jul
      // 2026). Picker-only types (device, stationery_qld) are excluded regardless.
      const anyItemPending = (updatedReq.children || []).some(function (child) {
        const items = [...(child.fees || []), child.uniforms, child.shoes, child.laptop];
        return items.some(function (it) {
          if (!it) return false;
          return !it.approvalStatus || it.approvalStatus === APPROVAL_STATUSES.PENDING;
        });
      });
      if (newStatus && !anyItemPending) {
        // Confirming clears the original "new_request" task plus any "new_comment" /
        // "new_file" task triggered by shelter activity on the same request.
        completeTasksForRef(
          req.refNo,
          ["new_request", "new_comment", "new_file"],
          confirmedAt
        ).then(function (res) {
          if (res.error) logger.warn("Auto-complete task failed:", res.error.message);
        });
      }

      // Picker tasks (device / stationery_qld) are now created by the SO queue
      // (queueSalesOrderForRequest → pickerTaskForOrder) so their granularity MIRRORS
      // the SO grouping: a per_child stationery rule yields one task per child pack, a
      // per_request device rule yields one multi-child task. See salesOrderCreation.ts.

      if (onSchoolLockerApproved) {
        let hasSLApproved = false;
        (updatedReq.children || []).forEach(function (child) {
          if (
            child.uniforms &&
            child.uniforms.supplier === "School Locker" &&
            child.uniforms.approvalStatus === APPROVAL_STATUSES.APPROVED
          )
            hasSLApproved = true;
        });
        if (hasSLApproved) onSchoolLockerApproved();
      }

      // Build an Unleashed Sales Order for THIS Confirm & Send — one order per
      // session, containing the items approved-and-confirmed in this session
      // (like picker tasks / action sheets). If nothing was newly approved, the
      // builder produces no lines and no order. Best-effort: never blocks/fails
      // the confirm.
      try {
        // Since db-182, save_request_children UPSERTS for internal callers, so EXISTING
        // line ids in updatedReq.children stay stable (no longer churned). But an item
        // ADDED at confirm is INSERTed with a fresh id that isn't in memory yet, and the
        // SO write-back stamps BY id — so we still reload to discover those new ids and
        // splice them (only the ids) into the confirmed in-memory tree, keeping this
        // session's approvalStatus/confirmedAt (which the engine filters on, and which a
        // DB timestamp round-trip could subtly reformat). For an existing item the spliced
        // id equals the one already in memory — a harmless no-op.
        let soChildren = updatedReq.children;
        try {
          const freshRes = await getRequestChildren(req.id);
          const fresh = Array.isArray(freshRes?.data) ? freshRes.data : null;
          if (fresh) {
            soChildren = (updatedReq.children || []).map(function (child, ci) {
              const fc = fresh[ci] || {};
              const merged = Object.assign({}, child);
              if (child.laptop && fc.laptop) {
                merged.laptop = Object.assign({}, child.laptop, { id: fc.laptop.id });
              }
              if (Array.isArray(child.fees)) {
                merged.fees = child.fees.map(function (f, fi) {
                  const ff = (fc.fees || [])[fi];
                  return ff ? Object.assign({}, f, { id: ff.id }) : f;
                });
              }
              return merged;
            });
          }
        } catch (e) {
          logger.warn("reload children for SO line ids failed:", e?.message || e);
        }
        await queueSalesOrderForRequest(
          {
            id: req.id,
            refNo: req.refNo,
            shelterCode: req.shelterCode,
            shelterName: req.shelterName,
            shelterEmail: req.shelterEmail,
            contactName: req.contactName,
            phone: req.phone,
            deliveryAddress: req.deliveryAddress,
            deliveryAddressId: req.deliveryAddressId,
            deliveryAddressOverride: req.deliveryAddressOverride,
            salesPersonEmail: user.email,
            state: req.state,
            children: soChildren,
          },
          confirmedAt
        );
      } catch (e) {
        logger.warn("queueSalesOrderForRequest failed:", e?.message || e);
      }

      // School Locker order — submitted HERE (at Confirm & Send), NOT on the
      // individual approval save, so nothing irreversible fires before the
      // responder confirms. The submission de-dupes per request, so a single call
      // covering all approved School Locker uniforms is safe.
      try {
        const hasApprovedSchoolLocker = (updatedReq.children || []).some(function (c) {
          return (
            c.uniforms &&
            c.uniforms.approvalStatus === APPROVAL_STATUSES.APPROVED &&
            c.uniforms.supplier === "School Locker"
          );
        });
        if (hasApprovedSchoolLocker && onSchoolLockerApproved) onSchoolLockerApproved();
      } catch (e) {
        logger.warn("School Locker submit at confirm failed:", e?.message || e);
      }

      // The SO write-back stamped the SO numbers onto the DB rows, NOT onto
      // updatedReq. The request only re-syncs from the DB on the leave/discard
      // path, which is skipped after a successful confirm — so without this the
      // in-memory tree (and every reopen that reuses it) never shows the SO
      // number until a full list refresh. Reload the children and merge just the
      // SO/Order numbers into the confirmed tree so they appear immediately.
      try {
        const stampedRes = await getRequestChildren(req.id);
        const stamped = Array.isArray(stampedRes?.data) ? stampedRes.data : null;
        if (stamped) {
          updatedReq.children = (updatedReq.children || []).map(function (child, ci) {
            const sc = stamped[ci] || {};
            const merged = Object.assign({}, child);
            if (child.laptop && sc.laptop) {
              merged.laptop = Object.assign({}, child.laptop, {
                deviceSoNumber: sc.laptop.deviceSoNumber,
                deviceOrderNumber: sc.laptop.deviceOrderNumber,
              });
            }
            if (Array.isArray(child.fees)) {
              merged.fees = child.fees.map(function (f, fi) {
                const sf = (sc.fees || [])[fi];
                return sf
                  ? Object.assign({}, f, {
                      feesSoNumber: sf.feesSoNumber,
                      feesOrderNumber: sf.feesOrderNumber,
                    })
                  : f;
              });
            }
            return merged;
          });
        }
      } catch (e) {
        logger.warn("reload children for SO numbers failed:", e?.message || e);
      }

      const postSaveSnapshot = buildPostSaveSnapshot(updatedReq);
      const savedReq = Object.assign({}, updatedReq, {
        notes: notesWithDrafts,
        status: newStatus || req.status,
        updatedBy: user.shelterName,
        updatedAt: confirmedAt,
        approvalsConfirmedAt: confirmedAt,
      });
      onSave(savedReq);
      // Signal a successful Confirm & Send to the parent — this already auto-clears
      // the request's open responder tasks (completeTasksForRef above), so the
      // request-detail exit prompt must NOT then also offer to mark them actioned.
      if (onConfirmed) onConfirmed();
      confirmedSnapshotRef.current = postSaveSnapshot;
      // Re-baseline the session-start snapshot so the NEXT confirmAndSend
      // treats decisions made in this session as the "starting point". Without
      // this, previously-declined items would re-appear in the next session's
      // summary comment as if newly declined.
      sessionStartApprovalsRef.current = postSaveSnapshot;
      decisionBaselineRef.current = snapshotDecisionBaseline(updatedReq.children);
      // Send the approval summary to the shelter automatically. The summary comment
      // was just saved to the request's notes; comment_to_shelter notifies the
      // shelter to log in and read it. THIS is the "& Send to Shelter" — the old
      // manual second step (a Send button in the panel) was removed weeks ago, but
      // the automatic send was never wired in, so the shelter was never emailed.
      if (req.shelterEmail) {
        sendEmail("comment_to_shelter", {
          requestId: req.id,
          refNo: req.refNo,
          shelterName: req.shelterName,
          shelterEmail: req.shelterEmail,
        });
      }
      // Open the comment panel so the responder can add an optional EXTRA comment
      // (sent via its own Add Comment button); the summary was already sent above.
      if (openCommentWithTemplate) openCommentWithTemplate(null, true);
      if (showSaved)
        showSaved(
          req.shelterEmail
            ? `Decisions confirmed ${GLYPH.check}`
            : `Decisions confirmed ${GLYPH.check} (no shelter email on file — not sent)`
        );
      if (repeatNoteFailed) {
        await alertDialog({
          title: "Repeat check note not saved",
          message:
            "Your decisions were confirmed, but the Repeat check note could not be saved.\n\n" +
            "Please add it as an internal comment on this request.",
        });
      }
      // Decision log (db-207) — LAST, after every critical step, and best-effort:
      // recordRequestDecisions never throws and is not awaited, so a logging failure
      // can't block, delay or undo this confirm (it is only logged as a warning).
      try {
        if (decisionRows.length) {
          recordRequestDecisions(req.id, confirmedAt, decisionRows).then(function (res) {
            if (res.error) logger.warn("Decision log not recorded:", res.error.message);
          });
        }
      } catch (e) {
        logger.warn("Decision log call failed:", e?.message || e);
      }
    } catch (e) {
      logger.error("confirmAndSend error:", e);
    } finally {
      setIsConfirming(false);
    }
  }

  // ── RENDER ────────────────────────────────────────────────────────────────

  // Width/padding kept in step with the per-fee selector in ResponderChildView
  // (John, 23 Aug 2026 — same control, so it should look the same on both screens).
  // See the sizing note there before trimming further: "Needs Info" is the binding
  // constraint.
  const _dropSt = {
    padding: "6px 6px",
    borderRadius: 6,
    border: "2px solid #CFE0DE",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "inherit",
    background: "#fff",
    width: 108,
    cursor: "pointer",
  };

  // Return null if no children
  if (!(req.children && req.children.length > 0)) return null;

  // Category display config for new schema
  const CHILD_CATS = [
    { key: "fees", label: "School Costs & Stationery" },
    { key: "uniforms", label: "Uniforms" },
    { key: "shoes", label: "Shoes" },
    { key: "laptop", label: "Device" },
  ];

  // D-2/D-4 terminal lock: a CONFIRMED Fully Processed request is finalised —
  // Revert (and per-item editing, in ResponderChildView) is disabled so no one
  // can reopen it. Keyed on confirmation, not the bare status, so pre-confirm
  // staging is never blocked.
  const locked = isLockedFullyProcessed(req);

  return (
    <div
      style={{
        border: `1px solid ${"#CFE0DE"}`,
        borderRadius: 10,
        marginBottom: 14,
        overflow: "hidden",
      }}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={function () {
          setApprovalsCollapsed(function (c) {
            return !c;
          });
        }}
        onKeyDown={function (e) {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setApprovalsCollapsed(function (c) {
              return !c;
            });
          }
        }}
        style={{
          background: "#1B5E20",
          color: "white",
          padding: "11px 18px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            fontWeight: 700,
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          Approval Decisions <HelpButton helpKey="approvals_overview" user={user} light={true} />
          <span style={{ fontSize: 12, fontWeight: 600, color: "#FFCDD2", fontStyle: "italic" }}>
            — summary of approval decisions; the actual decisions are made above when selecting each
            item for each child
          </span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {req.approvalsConfirmedAt && (
            <span
              style={{
                fontSize: 11,
                opacity: 0.9,
                background: "rgba(255,255,255,0.15)",
                borderRadius: 4,
                padding: "2px 8px",
              }}
            >
              Last confirmed:{" "}
              {new Date(req.approvalsConfirmedAt).toLocaleString("en-AU", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          )}
          <span style={{ fontSize: 11, opacity: 0.8 }}>
            {approvalsCollapsed ? "Expand" : "Collapse"}
          </span>
          {/* Arrow on the right — points DOWN when collapsed (click to expand
              downward) and UP when expanded (click to collapse upward). */}
          <span
            style={{ display: "inline-block", fontSize: 14, lineHeight: 1, marginLeft: 2 }}
            aria-hidden="true"
          >
            {approvalsCollapsed ? GLYPH.triangleDown : GLYPH.triangleUp}
          </span>
        </div>
      </div>
      {!approvalsCollapsed && (
        <div style={{ padding: "14px 14px 0" }}>
          <div style={{ fontSize: 11, color: "#607D79", marginBottom: 10 }}>
            Decision summary — use the controls on each item above to change statuses.
          </div>

          {(req.children || []).map(function (child, ci) {
            const childApp = approvals[ci] || {};
            const childLabel =
              "Child " +
              (ci + 1) +
              (child.schoolName ? ` — ${child.schoolName}` : "") +
              (child.yearLevel ? ` (${child.yearLevel})` : "");
            const childCats = CHILD_CATS.filter(function (cc) {
              if (cc.key === "fees") return (child.fees || []).length > 0;
              if (cc.key === "uniforms") return !!child.uniforms;
              if (cc.key === "shoes") return !!child.shoes;
              if (cc.key === "laptop") return !!child.laptop;
              return false;
            });
            if (childCats.length === 0) return null;
            return (
              <div
                key={child._key || ci}
                style={{ marginBottom: 16, borderLeft: "3px solid #1B5E20", paddingLeft: 10 }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#1B5E20",
                    marginBottom: 8,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  {childLabel}
                </div>
                {childCats.map(function (cc) {
                  if (cc.key === "fees") {
                    return (child.fees || []).map(function (f, fi) {
                      const curVal = childApp.fees?.[fi] || APPROVAL_STATUSES.PENDING;
                      const col = APPROVAL_COLOURS[curVal] || APPROVAL_COLOURS.Pending;
                      return (
                        <div
                          key={fi}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 6,
                            background: col.bg,
                            border: `1px solid ${col.border}`,
                            borderRadius: 7,
                            padding: "7px 10px",
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              fontSize: 12,
                              color: "#1A2E2B",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                            }}
                          >
                            <span>
                              {cc.label}
                              {f.feeType ? ` — ${f.feeType}` : ""}
                              {f.feeAmount ? ` (${money(f.feeAmount)})` : ""}
                            </span>
                            {isNewSinceConfirm(ci, "fees", fi) && (
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 700,
                                  background: "#FFF9C4",
                                  color: "#F57F17",
                                  border: "1px solid #F9A825",
                                  borderRadius: 3,
                                  padding: "1px 5px",
                                }}
                              >
                                New
                              </span>
                            )}
                          </div>
                          <span
                            style={{
                              padding: "4px 12px",
                              borderRadius: 6,
                              fontSize: 12,
                              fontWeight: 700,
                              background: col.bg,
                              color: col.text,
                              border: `1px solid ${col.border}`,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {curVal}
                          </span>
                        </div>
                      );
                    });
                  }
                  const catKey = cc.key;
                  const curVal = childApp[catKey] || APPROVAL_STATUSES.PENDING;
                  const col = APPROVAL_COLOURS[curVal] || APPROVAL_COLOURS.Pending;
                  let detail = "";
                  let catAmt = "";
                  if (catKey === "uniforms" && child.uniforms) {
                    detail = child.uniforms.supplier ? ` — ${child.uniforms.supplier}` : "";
                    const uniTotal = calcUniItems([child.uniforms]);
                    if (uniTotal > 0) catAmt = ` (${money(uniTotal)})`;
                  }
                  if (catKey === "shoes" && child.shoes) {
                    const parts = [];
                    if (child.shoes.jQty)
                      parts.push(`${child.shoes.jQty} jogger${child.shoes.jQty !== 1 ? "s" : ""}`);
                    if (child.shoes.lQty) parts.push(`${child.shoes.lQty} leather`);
                    if (parts.length) detail = ` — ${parts.join(" + ")}`;
                    const shoeTotal =
                      (child.shoes.jQty || 0) * (parseFloat(child.shoes.jCost) || 0) +
                      (child.shoes.lQty || 0) * (parseFloat(child.shoes.lCost) || 0);
                    if (shoeTotal > 0) catAmt = ` (${money(shoeTotal)})`;
                  }
                  if (catKey === "laptop" && child.laptop) {
                    detail = child.laptop.deviceType ? ` — ${child.laptop.deviceType}` : "";
                    const lapAmt = parseFloat(child.laptop.estimatedCost) || 0;
                    if (lapAmt > 0) catAmt = ` (${money(lapAmt)} est.)`;
                  }
                  return (
                    <div
                      key={catKey}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 6,
                        background: col.bg,
                        border: `1px solid ${col.border}`,
                        borderRadius: 7,
                        padding: "7px 10px",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          fontSize: 12,
                          color: "#1A2E2B",
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span>
                          {cc.label}
                          {detail}
                          {catAmt}
                        </span>
                        {isNewSinceConfirm(ci, catKey, 0) && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              background: "#FFF9C4",
                              color: "#F57F17",
                              border: "1px solid #F9A825",
                              borderRadius: 3,
                              padding: "1px 5px",
                            }}
                          >
                            New
                          </span>
                        )}
                      </div>
                      <span
                        style={{
                          padding: "4px 12px",
                          borderRadius: 6,
                          fontSize: 12,
                          fontWeight: 700,
                          background: col.bg,
                          color: col.text,
                          border: `1px solid ${col.border}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {curVal}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
      {/* Staged banner + Revert/Confirm actions — ALWAYS visible (only the decision
          summary above collapses with the arrow). */}
      <div style={{ padding: "12px 14px 14px" }}>
        {req.approvalsConfirmedAt === null &&
          (req.status === REQUEST_STATUSES.PART_PROCESSED ||
            req.status === REQUEST_STATUSES.FULLY_PROCESSED) && (
            <div
              style={{
                background: "#FFF8E1",
                border: "1px solid #FFB300",
                borderRadius: 7,
                padding: "8px 12px",
                marginBottom: 10,
                fontSize: 12,
                color: "#7c4a00",
                fontWeight: 600,
              }}
            >
              Decisions staged — not yet confirmed. Click Confirm & Send to DFV Service when ready.
            </div>
          )}
        {locked && (
          <div
            style={{
              background: "#E8F5E9",
              border: "1px solid #1B5E20",
              borderRadius: 7,
              padding: "8px 12px",
              marginBottom: 10,
              fontSize: 12,
              color: "#1B5E20",
              fontWeight: 600,
            }}
          >
            Fully processed — finalised and locked. Decisions can no longer be changed.
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
          <button
            onClick={revertToPending}
            disabled={isSaving || isConfirming || templatesLoading || locked}
            style={{
              flex: 1,
              background: "#9E9E9E",
              color: "white",
              border: "none",
              borderRadius: 7,
              padding: "9px 14px",
              fontSize: 13,
              fontWeight: 700,
              cursor:
                isSaving || isConfirming || templatesLoading || locked ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              opacity: isSaving || isConfirming || templatesLoading || locked ? 0.7 : 1,
            }}
          >
            {isSaving ? "Reverting..." : locked ? "Locked" : "Revert to Pending"}
          </button>
          <button
            onClick={async function () {
              let hasPending = false;
              (req.children || []).forEach(function (child, ci) {
                const childApp = approvals[ci] || {};
                if (
                  (child.fees || []).some(function (_f, fi) {
                    return childApp.fees?.[fi] === APPROVAL_STATUSES.PENDING;
                  })
                )
                  hasPending = true;
                if (child.uniforms && childApp.uniforms === APPROVAL_STATUSES.PENDING)
                  hasPending = true;
                if (child.shoes && childApp.shoes === APPROVAL_STATUSES.PENDING) hasPending = true;
                if (child.laptop && childApp.laptop === APPROVAL_STATUSES.PENDING)
                  hasPending = true;
              });
              if (
                hasPending &&
                !(await confirmDialog({ message: "Some items are still Pending. Confirm anyway?" }))
              )
                return;
              confirmAndSend();
            }}
            disabled={
              isConfirming ||
              isSaving ||
              templatesLoading ||
              !!req.approvalsConfirmedAt ||
              req.status === REQUEST_STATUSES.SUBMITTED ||
              req.status === REQUEST_STATUSES.DRAFT
            }
            style={{
              flex: 1,
              background:
                req.approvalsConfirmedAt ||
                req.status === REQUEST_STATUSES.SUBMITTED ||
                req.status === REQUEST_STATUSES.DRAFT
                  ? "#9E9E9E"
                  : "#1B5E20",
              color: "white",
              border: "none",
              borderRadius: 7,
              padding: "9px 14px",
              fontSize: 13,
              fontWeight: 700,
              cursor:
                isConfirming ||
                isSaving ||
                templatesLoading ||
                req.approvalsConfirmedAt ||
                req.status === REQUEST_STATUSES.SUBMITTED ||
                req.status === REQUEST_STATUSES.DRAFT
                  ? "not-allowed"
                  : "pointer",
              fontFamily: "inherit",
              opacity: isConfirming || isSaving || templatesLoading ? 0.7 : 1,
            }}
          >
            {isConfirming
              ? "Confirming..."
              : req.approvalsConfirmedAt
                ? `Confirmed ${GLYPH.check}`
                : req.status === REQUEST_STATUSES.SUBMITTED || req.status === REQUEST_STATUSES.DRAFT
                  ? "Nothing to confirm"
                  : "Confirm & Send to DFV Service"}
          </button>
        </div>
        <div style={{ fontSize: 11, color: "#607D79", lineHeight: 1.5, marginBottom: 8 }}>
          <strong>Revert to Pending</strong> clears all decisions and returns the request to
          Submitted — nothing sent to the DFV Service.{"  "}
          <strong>Confirm & Send</strong> finalises decisions and triggers auth docs, School Locker,
          picker tasks and the DFV Service Action Sheet.
          <HelpButton helpKey="approvals_confirm_send" user={user} light={false} />
          {req.approvalsConfirmedAt && (
            <span style={{ color: "#C62828" }}>
              {"  "}Confirmed. To change decisions contact your manager.
            </span>
          )}
        </div>
      </div>
      {repeatAsk && (
        <RepeatCheckNoteModal
          items={repeatAsk.items}
          onConfirm={function (text) {
            repeatAsk.resolve(text);
          }}
          onCancel={function () {
            repeatAsk.resolve(null);
          }}
        />
      )}
    </div>
  );
}
